import {
  cpfDigits,
  hasAddress,
  isValidCpf,
  memberPhoneError,
  memberProfileErrors,
  normalizeAddress,
  normalizeEmail,
  phoneDigits,
  showsOriginChurch,
  MAX_NICKNAME,
} from './members';
import { isActiveMember } from './membership';
import { memberInScope, type Permissions } from './permissions';
import type { AvailState, Member } from './types';

/**
 * O link pessoal para completar o cadastro: quem entrou pela planilha (só com o nome e o
 * sexo) recebe da liderança um link — copiado ou aberto no WhatsApp com a mensagem pronta —
 * e preenche os próprios dados sem precisar de conta. O que a pessoa salva vale na hora;
 * criar o acesso (e-mail e senha) é opcional, no fim.
 *
 * O link é a credencial (como o da agenda): vale `PROFILE_LINK_DAYS`, um novo invalida o
 * anterior e criar a conta o encerra. Como ele pode ser repassado, o formulário **não
 * mostra** o que é privado ou de contato já guardado (CPF, endereço, contato de emergência,
 * e-mail e telefone): só diz que já foi informado; em branco, fica como está.
 */

/** Quantos dias o link vale. */
export const PROFILE_LINK_DAYS = 30;

/** O caminho na aplicação: `/{igreja}/completar/{token}`. */
export function profileLinkPath(slug: string, token: string): string {
  return `/${slug}/completar/${token}`;
}

/** Quem pode receber o link: ativo e sem acesso (com acesso, ele mesmo edita o perfil). */
export function canSendProfileLink(member: Member, hasAccess: boolean): boolean {
  return isActiveMember(member) && !hasAccess;
}

/**
 * Quem gera o link: o Administrador, para qualquer um; o líder, para quem está no seu
 * recorte (habilitado nas funções que ele escala) — o que vale para a lista de Membros.
 */
export function canShareProfileLink(permissions: Permissions, member: Member): boolean {
  return permissions.isAdmin || (permissions.isManager && memberInScope(member, permissions.scope));
}

/** A mensagem pronta, para copiar ou abrir no WhatsApp. */
export function profileLinkMessage(ctx: {
  orgName: string;
  memberName: string;
  sender: string;
  url: string;
}): string {
  const first = ctx.memberName.trim().split(/\s+/)[0] ?? '';
  const greeting = first ? `Olá, ${first}!` : 'Olá!';

  return (
    `${greeting} Aqui é ${ctx.sender}, da ${ctx.orgName}. Para organizarmos as escalas, complete seu ` +
    `cadastro neste link (leva uns 3 minutos, não precisa de senha): ${ctx.url}\n` +
    `Se quiser, no fim você cria seu acesso ao app para ver suas escalas.`
  );
}

/** O que ainda falta no cadastro, para ler ("Telefone, Data de nascimento…"). */
export function profileGaps(member: Member): string[] {
  const gaps: [boolean, string][] = [
    [!member.phone, 'Telefone'],
    [!member.email, 'E-mail'],
    [!member.birthDate, 'Data de nascimento'],
    [!hasAddress(member.address), 'Endereço'],
    [member.availability.every((v) => v === 'unset'), 'Disponibilidade'],
  ];

  return gaps.filter(([missing]) => missing).map(([, label]) => label);
}

/** O que o formulário do link edita. Contato e privado em branco = fica como está. */
export type SelfProfile = Pick<
  Member,
  | 'name'
  | 'nickname'
  | 'birthDate'
  | 'sex'
  | 'phone'
  | 'email'
  | 'civilStatus'
  | 'profession'
  | 'churchStatus'
  | 'memberSince'
  | 'joinedVia'
  | 'baptismDate'
  | 'originChurch'
  | 'cpf'
  | 'address'
  | 'emergency'
  | 'availability'
>;

/** O que já está guardado e não volta para o formulário — só a pista de que existe. */
export interface SelfProfileKnown {
  /** O e-mail mascarado ("a•••@gmail.com"), `''` sem e-mail. */
  email: string;
  /** O fim do telefone ("•••• 4567"), `''` sem telefone. */
  phone: string;
  cpf: boolean;
  address: boolean;
  emergency: boolean;
}

/** "ana@gmail.com" → "a•••@gmail.com". */
export function maskEmail(email: string): string {
  const [user = '', domain = ''] = email.split('@');

  return email ? `${user.slice(0, 1)}•••@${domain}` : '';
}

/** O formulário aberto: os dados que podem voltar e as pistas do resto. */
export function selfProfileOf(member: Member): { profile: SelfProfile; known: SelfProfileKnown } {
  return {
    profile: {
      name: member.name,
      nickname: member.nickname,
      birthDate: member.birthDate,
      sex: member.sex,
      phone: '',
      email: '',
      civilStatus: member.civilStatus,
      profession: member.profession,
      churchStatus: member.churchStatus,
      memberSince: member.memberSince,
      joinedVia: member.joinedVia,
      baptismDate: member.baptismDate,
      originChurch: member.originChurch,
      cpf: '',
      address: { cep: '', street: '', number: '', complement: '', district: '', city: '', uf: '' },
      emergency: { name: '', relation: '', phone: '' },
      availability: member.availability,
    },
    known: {
      email: maskEmail(member.email),
      phone: member.phone ? `•••• ${member.phone.slice(-4)}` : '',
      cpf: !!member.cpf,
      address: hasAddress(member.address),
      emergency: !!member.emergency.name || !!member.emergency.phone,
    },
  };
}

export type SelfProfileErrors = Partial<
  Record<
    | 'name'
    | 'nickname'
    | 'sex'
    | 'phone'
    | 'email'
    | 'cpf'
    | 'cep'
    | 'uf'
    | 'emergencyPhone'
    | 'memberSince'
    | 'baptismDate',
    string
  >
>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * As regras do formulário (as mesmas do Salvar do perfil): nome e sexo obrigatórios,
 * telefone com DDD, e-mail e CPF válidos quando informados, datas e endereço. A repetição
 * de nome, e-mail e CPF com outro membro quem confere é o servidor.
 */
export function selfProfileErrors(profile: SelfProfile, today: string): SelfProfileErrors {
  const errors: SelfProfileErrors = {};
  const phone = memberPhoneError(profile.phone);
  const email = normalizeEmail(profile.email);
  const cpf = cpfDigits(profile.cpf);

  if (!profile.name.trim()) errors.name = 'Informe seu nome.';
  if (profile.nickname.trim().length > MAX_NICKNAME)
    errors.nickname = `No máximo ${MAX_NICKNAME} caracteres.`;
  if (profile.sex !== 'F' && profile.sex !== 'M') errors.sex = 'Escolha uma opção.';
  if (phone) errors.phone = phone;
  if (email && (!EMAIL.test(email) || email.length > 254)) errors.email = 'E-mail inválido.';
  if (cpf && (cpf.length !== 11 || !isValidCpf(cpf))) errors.cpf = 'CPF inválido.';

  return { ...errors, ...memberProfileErrors(profile, today) };
}

/** Os 21 períodos. */
const SLOTS = 21;

/**
 * O cadastro com o que a pessoa salvou pelo link: os dados que voltaram ao formulário
 * entram como vieram; contato e privado em branco ficam como estavam (o formulário não os
 * mostrou). Funções, cônjuge, foto e situação não mudam por aqui.
 */
export function applySelfProfile(member: Member, profile: SelfProfile): Member {
  const keep = <T>(typed: T, blank: boolean, saved: T) => (blank ? saved : typed);
  const address = normalizeAddress(profile.address);
  const emergency = {
    name: profile.emergency.name.trim(),
    relation: profile.emergency.relation.trim(),
    phone: phoneDigits(profile.emergency.phone),
  };

  return {
    ...member,
    name: profile.name.trim(),
    nickname: profile.nickname.trim(),
    birthDate: profile.birthDate,
    sex: profile.sex,
    phone: keep(phoneDigits(profile.phone), !phoneDigits(profile.phone), member.phone),
    email: keep(normalizeEmail(profile.email), !normalizeEmail(profile.email), member.email),
    civilStatus: profile.civilStatus,
    profession: profile.profession.trim(),
    churchStatus: profile.churchStatus,
    memberSince: profile.memberSince,
    joinedVia: profile.joinedVia,
    baptismDate: profile.baptismDate,
    originChurch: showsOriginChurch(profile.joinedVia) ? profile.originChurch.trim() : '',
    cpf: keep(cpfDigits(profile.cpf), !cpfDigits(profile.cpf), member.cpf),
    address: keep(address, !hasAddress(address), member.address),
    emergency: keep(emergency, !emergency.name && !emergency.phone && !emergency.relation, member.emergency),
    availability:
      profile.availability.length === SLOTS ? (profile.availability as AvailState[]) : member.availability,
  };
}

/** Busca da lista de Membros: o cadastro ainda incompleto (alguma lacuna de `profileGaps`). */
export function isIncomplete(member: Member): boolean {
  return profileGaps(member).length > 0;
}
